<template>
  <v-container>
    <v-row justify="center">
      <v-col cols="12">
        <v-card class="pa-4" elevation="4">
          <v-card-title>
            {{ isEditing ? 'Editar Empresa' : 'Nova Empresa' }}
          </v-card-title>

          <v-card-text>
            <v-tabs v-model="currentTab" dark class="mb-6">
              <v-tab value="details">Detalhes e Contato</v-tab>
              <v-tab value="payment">Configurações de Pagamento</v-tab>
            </v-tabs>

            <v-form ref="formRef" @submit.prevent="handleSubmit">
              <v-window v-model="currentTab" class="mt-2">
                <v-window-item value="details">
                  <v-row class="mt-2">
                    <v-col cols="12" sm="6">
                      <v-text-field v-model="formValue.name" label="Nome / Razão Social" :rules="[rules.name]" />
                    </v-col>
                    <v-col cols="12" sm="3">
                      <v-text-field v-model="formValue.document" v-maska="'##.###.###/####-##'"
                        label="CNPJ" :rules="[rules.document]" />
                    </v-col>
                    <v-col cols="12" sm="3">
                      <v-text-field v-model="formValue.email" label="Email de Contato" :rules="[rules.email]" />
                    </v-col>

                    <v-col cols="12" sm="3">
                      <v-text-field v-model="formValue.phone" v-maska="'(##) #####-####'" label="Telefone" />
                    </v-col>
                    <v-col cols="12" sm="3">
                        <v-switch v-model="formValue.is_active" color="success" label="Ativa?" />
                    </v-col>
                  </v-row>

                  <v-divider class="my-4" />

                  <h4 class="text-h6 mb-4">Localização</h4>
                  <v-row>
                    <v-col cols="12" sm="3">
                      <v-text-field v-model="formValue.zip_code" v-maska="'#####-###'" label="CEP"
                        @blur="consultaCEP" :loading="isCepLoading" />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field v-model="formValue.address" label="Endereço" />
                    </v-col>
                    <v-col cols="12" sm="3">
                      <v-text-field v-model="formValue.number" label="Número" />
                    </v-col>
                    <v-col cols="12" sm="3">
                      <v-text-field v-model="formValue.neighborhood" label="Bairro" />
                    </v-col>
                    <v-col cols="12" sm="3">
                      <v-text-field v-model="formValue.complement" label="Complemento" />
                    </v-col>
                    <v-col cols="12" sm="4">
                      <v-text-field v-model="formValue.city" label="Cidade" />
                    </v-col>
                    <v-col cols="12" sm="2">
                      <v-text-field v-model="formValue.state" label="UF" />
                    </v-col>
                  </v-row>

                </v-window-item>

                <v-window-item value="payment">
                  <v-row>
                    <v-col cols="12">
                      <p class="mb-4 text-body-1">Estas configurações são usadas para integração recebimentos.</p>
                    </v-col>

                    <v-col cols="12" sm="6">
                      <v-text-field v-model="formValue.public_key" label="Chave Pública (API Key)" />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field v-model="formValue.access_token" label="Token de Acesso (Access Token)" />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field v-model="formValue.pix_key" label="Chave PIX de Recebimento" />
                    </v-col>

                  </v-row>
                </v-window-item>

              </v-window>
            </v-form>
          </v-card-text>

          <v-card-actions class="d-flex justify-space-between">
            <v-btn color="secondary" variant="flat" @click="handleCancel">
              <v-icon left>mdi-arrow-left</v-icon>
              Voltar
            </v-btn>
            <v-btn color="primary" variant="flat" :loading="isSubmitting" :disabled="isSubmitting"
              @click="handleSubmit">
              <v-icon left>mdi-content-save</v-icon>
              {{ isEditing ? 'Salvar Alterações' : 'Cadastrar Empresa' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { vMaska } from "maska/vue";
import api from '@/services/api';
import { useToastStore } from '@/store/toast';
import { useLoadingStore } from '@/store/loadingStore';

const toastStore = useToastStore();
const loadingStore = useLoadingStore();
const route = useRoute();
const router = useRouter();
const formRef = ref(null);

const isLoading = ref(false);
const isSubmitting = ref(false);
const isCepLoading = ref(false);

const currentTab = ref('details');

const isEditing = computed(() => !!route.params.id);

const formValue = ref({
  name: '',
  document: '',
  phone: '',
  email: '',
  is_active: true,
  zip_code: '',
  address: '',
  number: '',
  complement: '',
  neighborhood: '',
  city: '',
  state: '',
  public_key: null,
  access_token: null,
  pix_key: null,
});

const rules = {
  name: v => !!v || 'O nome é obrigatório.',
  document: v => (v && String(v).replace(/\D/g, '').length === 14) || 'O CNPJ deve ser válido.',
  email: v => !v || /.+@.+\..+/.test(v) || 'O email deve ser válido.',
};

const loadEnterprise = async (id) => {
  isLoading.value = true;
  loadingStore.show();
  try {
    const response = await api.get(`/enterprises/${id}`);
    const data = response.data.data;

    if (!data) {
      toastStore.showToast({ message: 'A empresa não foi encontrada.', color: 'warning' });
      return;
    }

    formValue.value = {
      ...formValue.value,
      ...data,
      is_active: data.is_active === 1 || data.is_active === true,
    };

    await nextTick();
    if (formRef.value) formRef.value.resetValidation();
  } catch (error) {
    console.error('Erro ao carregar os dados da empresa:', error);
    toastStore.showToast({ message: error?.response?.data?.error || 'Erro ao carregar os dados da empresa.', color: 'error' });
  } finally {
    isLoading.value = false;
    loadingStore.hide();
  }
};

const handleSubmit = async () => {
  const { valid } = await formRef.value.validate();
  if (!valid) {
    currentTab.value = 'details';
    return;
  }

  isSubmitting.value = true;
  const payload = { ...formValue.value };

  // Limpa máscaras
  if (payload.document) payload.document = String(payload.document).replace(/\D/g, '');
  if (payload.phone) payload.phone = String(payload.phone).replace(/\D/g, '');
  if (payload.zip_code) payload.zip_code = String(payload.zip_code).replace(/\D/g, '');

  // Converte boolean para 1/0
  payload.is_active = payload.is_active ? 1 : 0;

  try {
    let response;

    if (isEditing.value) {
      response = await api.put(`/enterprises/${route.params.id}`, payload);
    } else {
      response = await api.post('/enterprises', payload);
    }

    router.push('/app/empresa');
    toastStore.showToast({
      message: isEditing.value ? 'Empresa atualizada com sucesso!' : 'Empresa cadastrada com sucesso!',
      color: 'success',
    });
  } catch (error) {
    console.error('Erro ao salvar empresa:', error);
    toastStore.showToast({ message: error?.response?.data?.error || 'Erro ao salvar empresa.', color: 'error' });
  } finally {
    isSubmitting.value = false;
  }
};

async function consultaCEP() {
  const cep = formValue.value.zip_code ? String(formValue.value.zip_code).replace(/\D/g, '') : null;
  if (!cep || cep.length !== 8) return;

  isCepLoading.value = true;
  try {
    const response = await api.get('/cep', { params: { cep } });
    if (response.data && !response.data.erro) {
      formValue.value.address = response.data.logradouro;
      formValue.value.neighborhood = response.data.bairro;
      formValue.value.city = response.data.localidade;
      formValue.value.state = response.data.uf;
      toastStore.showToast({ message: 'CEP encontrado com sucesso!', color: 'success' });
    } else {
      toastStore.showToast({ message: 'CEP não encontrado. Preencha manualmente.', color: 'warning' });
    }
  } catch (err) {
    console.error('Erro consulta CEP:', err);
    toastStore.showToast({ message: 'Erro na consulta de CEP.', color: 'error' });
  } finally {
    isCepLoading.value = false;
  }
}

const handleCancel = () => { router.push('/app/empresa'); };

onMounted(() => {
  if (isEditing.value) {
    loadEnterprise(route.params.id);
  }
});
</script>

<style scoped>
/* Estilos mantidos do modelo */
</style>